import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { PetlocMark } from "../../components/PetlocMark";

export function AppShell() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    await qc.invalidateQueries({ queryKey: ["me"] });
    nav("/");
  };

  return (
    <div className="min-h-dvh flex flex-col bg-[#FBF8F5]">
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-sm border-b border-[#ECE3E7]">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-4">
          <Link to="/app" className="flex items-center gap-2 font-display text-lg font-medium text-[#1B0E16]">
            <span className="text-brand-600"><PetlocMark size={28} /></span>
            {t("app.name")}
          </Link>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            <NavTab to="/app">{t("nav.dashboard")}</NavTab>
            <NavTab to="/app/sms">{t("nav.sms")}</NavTab>
            <NavTab to="/app/settings">{t("nav.settings")}</NavTab>
            {me?.role === "ADMIN" && <NavTab to="/admin/orders">{t("nav.admin")}</NavTab>}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <button className="btn-ghost" onClick={logout}>{t("nav.logout")}</button>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6">
        {me && !me.emailVerifiedAt && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            {t("auth.verifySent")}
          </div>
        )}
        <Outlet />
      </main>
      <footer className="border-t border-[#ECE3E7] py-4 text-center text-xs text-[#8A7A82]">
        <Link to="/impressum" className="mx-2">{t("legal.impressum")}</Link>·
        <Link to="/datenschutz" className="mx-2">{t("legal.datenschutz")}</Link>·
        <Link to="/agb" className="mx-2">{t("legal.agb")}</Link>
      </footer>
    </div>
  );
}

function NavTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/app"}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-brand-600 text-white" : "text-[#5C4D55] hover:bg-[#FBEEFB]"}`
      }
    >
      {children}
    </NavLink>
  );
}
